<template>
  <div>
    <meeting-canceled-modal
      :is-visible.sync="isMeetingCanceledModalDisplayed" />
    <carousel ref="carousel" :index.sync="slideIndex" :slides="2" class="carousel">
      <carouselCard>
        <div class="carousel__card" :class="{'no-buttons': !isBouttonsDisplayed }">
          <component :is="meetingDisplayComponent" :isBouttonsDisplayed="isBouttonsDisplayed" />
          <div class="carousel__card__footer" v-if="isBouttonsDisplayed">
            <el-button
              v-if="!demand.meeting || demand.meeting.meeting_type !== 'MEETING_TYPE_VAD'"
              :class="{ 'button--blank': demand.meeting }"
              type="primary"
              @click="next">
              {{
                demand.meeting
                  ? $t('home.meeting.meetingScheduled.modifyButton')
                  : $t('home.meeting.meetingNotScheduled.scheduleButton')
              }}
            </el-button>
            <el-button
              v-if="demand.meeting"
              :loading="isMeetingLoading"
              :disabled="isMeetingLoading"
              class="button--danger"
              type="primary"
              @click="cancel">
              {{ $t('home.meeting.meetingScheduled.cancelButton') }}
            </el-button>
          </div>
        </div>
      </carouselCard>
      <carouselCard>
        <div class="carousel__card">
          <h3 class="carousel__card__title">
            {{ $t('home.meeting.meetingSchedule.title') }}
          </h3>
          <el-row :gutter="10">
            <el-col :xs="24" :sm="24" :md="24" :lg="14" :xl="12">
              <appointment-booking
                v-model="meeting"
                :date-picker-date.sync="datePickerDate"
                :meetings-by-days="meetingsSlots"
                :loading="isMeetingsSlotsLoading"
                :calendar-options="calendarOptions"
                @datePickerDateChange="dateChange" />
            </el-col>
            <el-col :xs="24" :sm="24" :md="24" :lg="10" :xl="12">
              <div
                class="carousel__card__panel"
                :class="{ 'carousel__card__panel--opened': meeting }">
                <p v-if="!meeting">
                  {{ $t('home.meeting.meetingSchedule.subtitle', { time: displayTime }) }}
                </p>
                <transition name="el-fade-in" mode="out-in">
                  <div v-if="meeting">
                    <p>{{ $t('home.meeting.meetingSchedule.meetingDescription') }}</p>
                    <p class="carousel__card__panel__meeting">{{ newMeetingDate }}</p>
                    <div class="carousel__card__panel__button-wrapper">
                      <el-button
                      type="success"
                      @click="SelectMeeting"
                      :disabled="isMeetingLoading"
                      :loading="isMeetingLoading">
                      {{ $t('home.meeting.meetingSchedule.btn') }}
                    </el-button>
                  </div>
                  </div>
                </transition>
              </div>
            </el-col>
          </el-row>
          <div class="carousel__card__footer">
            <el-button
              type="primary"
              class="button--blank"
              :disabled="isMeetingLoading"
              @click="previous">
              {{ $t('base.back') }}
            </el-button>
          </div>
        </div>
      </carouselCard>
    </carousel>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AppointmentBooking from 'meeting-component';
import Carousel from '@/utils/components/Carousel.vue';
import CarouselCard from '@/utils/components/CarouselCard.vue';
import MeetingCanceledModal from '@/components/home/meeting/MeetingCanceledModal.vue';
import 'meeting-component/dist/lib/appointment-booking.css';
import breakpoints from '@/assets/scss/breakpoints-export.scss';

export default {
  name: 'meeting',
  components: {
    AppointmentBooking,
    Carousel,
    CarouselCard,
    MeetingCanceledModal,
  },
  data() {
    return {
      isMeetingCanceledModalDisplayed: false,
      slideIndex: 0,
      datePickerDate: new Date(),
      meeting: null,
    };
  },
  computed: {
    ...mapGetters({
      projectSelected: 'projectStore/projectSelected',
      demand: 'demandStore/demand',
      isMeetingsSlotsLoading: 'projectStore/isMeetingsSlotsLoading',
      meetingsSlots: 'projectStore/meetingsSlots',
      isMeetingLoading: 'projectStore/isMeetingLoading',
    }),
    isBouttonsDisplayed() {
      if (this.demand.meeting) {
        const date = new Date(
          new Date(
            this.demand.meeting.meeting_date,
          ).getTime() - 15 * 60 * 1000,
        );
        return date > new Date();
      }
      return true;
    },
    durationMinutes() {
      if (this.demand.meeting) {
        return this.demand.meeting.duration_minutes + this.demand.meeting.duration_hours * 60;
      }
      return 45;
    },
    displayTime() {
      if (this.demand.meeting) {
        const minutes = this.demand.meeting.duration_minutes < 10
          ? `0${this.demand.meeting.duration_minutes}`
          : this.demand.meeting.duration_minutes;
        const hours = this.demand.meeting.duration_hours;
        return hours
          ? `${hours}${this.$t('base.hoursUnit')}${minutes}`
          : `${minutes} ${this.$t('base.minutesUnit')}`;
      }
      return `${this.durationMinutes} ${this.$t('base.minutesUnit')}`;
    },
    meetingDisplayComponent() {
      return this.demand.meeting
        ? () => import('./meeting/MeetingDisplay.vue')
        : () => import('./meeting/NoMeetingDisplay.vue');
    },
    newMeetingDate() {
      const date = this.formatDate(this.meeting.start);
      const startHours = this.formatHours(this.meeting.start);
      const endHours = this.formatHours(this.meeting.end);
      return this.$t('home.meeting.meetingSchedule.newMeeting', {
        date,
        startHours,
        endHours,
      });
    },
    calendarOptions() {
      return {
        daysLabel: [
          this.$t('base.days.monday'),
          this.$t('base.days.tuesday'),
          this.$t('base.days.wednesday'),
          this.$t('base.days.thursday'),
          this.$t('base.days.friday'),
          this.$t('base.days.saturday'),
        ],
        monthsLabel: [
          this.$t('base.monthsMin.january'),
          this.$t('base.monthsMin.february'),
          this.$t('base.monthsMin.march'),
          this.$t('base.monthsMin.april'),
          this.$t('base.monthsMin.may'),
          this.$t('base.monthsMin.june'),
          this.$t('base.monthsMin.july'),
          this.$t('base.monthsMin.august'),
          this.$t('base.monthsMin.september'),
          this.$t('base.monthsMin.october'),
          this.$t('base.monthsMin.november'),
          this.$t('base.monthsMin.december'),
        ],
        backgroundColors: '#CEEBE9',
        loadingText: this.$t('home.meeting.loadingText'),
        emptyDate: this.$t('home.meeting.emptyDate'),
      };
    },
  },
  methods: {
    ...mapActions({
      getMeetingsSlots: 'projectStore/getMeetingsSlots',
      postMeeting: 'projectStore/postMeeting',
      deleteMeeting: 'projectStore/deleteMeeting',
    }),
    formatDate(date) {
      const d = new Date(date);
      const day = d.getDate() < 10 ? `0${d.getDate()}` : d.getDate();
      const month = d.getMonth() + 1 < 10 ? `0${d.getMonth() + 1}` : d.getMonth() + 1;
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    formatHours(date) {
      const d = new Date(date);
      const hours = d.getHours() < 10 ? `0${d.getHours()}` : d.getHours();
      const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
      return `${hours}H${minutes}`;
    },
    dateChange({ date }) {
      try {
        this.getMeetingsSlots({
          days: 7,
          duration: this.durationMinutes,
          date: this.formatDateWithTimezone(date),
        });
      // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    SelectMeeting() {
      this.postMeeting(this.meeting).then(() => {
        this.previous();
      }).catch(() => {});
    },
    cancel() {
      this.isMeetingCanceledModalDisplayed = true;
    },
    next() {
      if (window.innerWidth < parseInt(breakpoints.tablet, 10) + 1) {
        document.querySelector('.carousel__card').scrollIntoView();
      }
      try {
        this.getMeetingsSlots({
          days: 7,
          duration: this.durationMinutes,
          date: this.formatDateWithTimezone(this.datePickerDate),
        });
      // eslint-disable-next-line no-empty
      } catch (e) {}
      this.$refs.carousel.next();
    },
    previous() {
      this.meeting = null;
      this.$refs.carousel.previous();
    },
    formatNumber(number) {
      return number > 9 ? number : `0${number}`;
    },
    formatDateWithTimezone(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = this.formatNumber(d.getMonth() + 1);
      const days = this.formatNumber(d.getDate());
      const hours = this.formatNumber(d.getHours());
      const tz = d.getTimezoneOffset() / 60;
      const absoluteTz = this.formatNumber(Math.abs(tz));
      const tzoffset = tz < 0 ? `+${absoluteTz}:00` : `-${absoluteTz}:00`;
      return `${year}-${month}-${days}T${hours}:00:00${tzoffset}`;
    },
  },
  watch: {
    projectSelected() {
      this.previous();
    },
  },
};
</script>

<style scoped lang="scss">
.carousel {
  &__card {
    min-height: 430px;
    height: 100%;
    padding-bottom: 80px;
    position: relative;
    &__title {
      margin-bottom: 40px;
    }
    &__button {
      margin-top: 20px;
      text-align: center;
    }
    &__panel {
      border-radius: 4px;
      padding: 20px;
      margin: 20px 30px;
      max-width: 520px;
      text-align: center;
      background-color: $--color-light-grey;
      @include breakpoint(mobile) {
        margin: 20px auto;
      }
      @include breakpoint(desktop) {
        text-align: left;
        margin: auto;
      }
      @include breakpoint(wide) {
        margin: 0;
        min-height: 260px;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      &__meeting {
        text-align: center;
        align-items: center;
        font-size: 25px;
        font-weight: 700;
      }
      &__button-wrapper {
        text-align: center;
      }
    }
    &__footer {
      position: absolute;
      width: 100%;
      text-align: center;
      bottom: 20px;

      .el-button {
        margin-top: 10px;
      }
    }
  }
  .no-buttons {
    min-height: inherit;
    padding-bottom: 0;
  }
}
</style>
